import React, { useMemo } from 'react';
import style from './style.module.scss';
import { OptionProps } from '../Select';
import { useValidationFeedback } from '../../components/ValidationFeedbackFormik';
import CheckBox from '../Input/CheckBox';
import RadioButton from '../Input/RadioButton';
import { ErrorMessage, NotificationIcon, SelectionSet } from '..';
import { useField } from 'formik';
import { joinNames } from '../helpers';
import type { BaseFieldProps, UiState, UiStateProps } from '../Input';
import useDefaultId from '@n2-common/lib/useDefaultId';

export type InputGroupProps<TType extends 'radio' | 'checkbox' = 'radio'> = Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    'value' | 'defaultValue'
> & {
    options: (OptionProps & { helptext?: string | undefined | JSX.Element | null })[];
    type: TType;
    horisontal?: boolean;
    value: ValueType<TType>;
} & BaseFieldProps;

export function InputGroup<TType extends 'radio' | 'checkbox'>({
    type,
    name,
    id,
    label,
    error,
    options,
    uistate = 'standard',
    value,
    horisontal,
    disabled,
    onChange,
    onInput,
    onBlur,
    helptext,
    className: outerClassnme,
}: InputGroupProps<TType> & UiStateProps) {
    const compId = useDefaultId(id);
    const className = joinNames(
        outerClassnme,
        style.root,
        error ? style.error : undefined,
        getStateClassName(uistate),
        
    );
    const classNameButtons = horisontal ? style.horisontalButtons : undefined
    return (
        <>
            <SelectionSet legend={label} className={className} id={compId} helptext={helptext}>
                <div className={style.horisontalSelectionSetContainer}>
                    <div className={classNameButtons}>
                        {options.map(({ value: optionvalue, text, helptext }, i) => {
                            const attr = {
                                key: `${i}`,
                                name,
                                label: text,
                                helptext,
                                value: optionvalue,
                                checked: type == 'radio' ? optionvalue == value : Array.isArray(value) && value.includes(optionvalue),
                                disabled,
                                id: `${compId}${i}`,
                                onChange,
                                onBlur,
                                onInput,
                            };
                            return type == 'checkbox' ? <CheckBox {...attr} /> : <RadioButton {...attr} />;
                        })}
                    </div>
                    <div className={style.bottomRow}>
                        <ErrorMessage>{error}</ErrorMessage>
                        <NotificationIcon prefixForUiTesting={name} check={uistate == 'value-verified'} alert={!!error && { title: error }} />
                    </div>                 
            </div>               
        </SelectionSet>
        </>
    );
}

function getStateClassName(uistate: UiState) {
    switch (uistate) {
        case 'value-verified':
            return style.verified;

        default:
            return undefined;
    }
}

type ValueType<T extends 'radio' | 'checkbox'> = T extends 'radio' ? string : T extends 'checkbox' ? string[] : never;

export function FormikInputGroup<TType extends 'radio' | 'checkbox'>({
    name,
    type,
    ...attr
}: Omit<InputGroupProps<TType>, 'onChange' | 'onBlur' | 'onInput' | 'value'>) {
    const [{ onBlur, value }, { error, touched }, { setValue }] = useField<ValueType<TType>>({
        name,
        multiple: type == 'checkbox',
    });
    const [verified, handleInput] = useValidationFeedback(name);
    const uistate = useMemo(() => (verified ? 'value-verified' : 'standard'), [verified]);
    const onChange = useMemo<React.ChangeEventHandler<HTMLInputElement>>(() => {
        return type == 'radio'
            ? (e) => setValue(e.currentTarget.value as ValueType<TType>)
            : (e) => {
                  const checked = (Array.from(document.getElementsByName(e.target.name)) as HTMLInputElement[])
                      .filter((el) => el.checked)
                      .map((el) => el.value);
                  setValue(checked as ValueType<TType>);
              };
    }, [setValue, type]);

    return (
        <InputGroup
            name={name}
            type={type}
            value={value}
            uistate={uistate}
            onInput={handleInput}
            onBlur={onBlur}
            onChange={onChange}
            error={touched && error}
            {...attr}
        />
    );
}
